// 🎯 SIMPLE MIXPANEL TRACKING - Everything in one file!
import mixpanel from 'mixpanel-browser';

// Initialize Mixpanel (call this once in index.js)
export const initMixpanel = () => {
  const token = process.env.REACT_APP_MIXPANEL_TOKEN;
  const debug = process.env.REACT_APP_MIXPANEL_DEBUG === 'true';

  if (!token) {
    console.warn('Mixpanel token not found');
    return null;
  }

  mixpanel.init(token, {
    debug: debug || process.env.NODE_ENV === 'development',
    persistence: 'localStorage',
    
    // COMPLETELY DISABLE Autocapture to prevent [Auto] Element Click events
    autocapture: false,
    track_links: false,
    track_forms: false,
    track_pageview: false,
    
    // Disable all automatic tracking
    track_links_timeout: 0,
    track_forms_timeout: 0,
    
    // Disable session recording autocapture
    record_sessions_percent: 0, // Set to 0 to disable session recording
    
    // Disable heatmap data collection (can cause autocapture)
    record_heatmap_data: false,
    
    // Disable performance tracking (can cause autocapture)
    track_performance: false,
    
    // Additional settings to prevent autocapture
    disable_persistence: false,
    disable_cookie: false,
    secure_cookie: true,
    
    // Disable automatic event collection
    ignore_dnt: true,
    loaded: function(mixpanel) {
      // Explicitly disable autocapture after initialization
      if (mixpanel.config) {
        mixpanel.config.autocapture = false;
        mixpanel.config.track_links = false;
        mixpanel.config.track_forms = false;
        mixpanel.config.track_pageview = false;
      }
    }
  });

  // Additional step to ensure autocapture is completely disabled
  setTimeout(() => {
    if (mixpanel && mixpanel.config) {
      mixpanel.config.autocapture = false;
      mixpanel.config.track_links = false;
      mixpanel.config.track_forms = false;
      mixpanel.config.track_pageview = false;
      console.log('🔒 Autocapture explicitly disabled after initialization');
    }
  }, 100);

  console.log('✅ Mixpanel initialized with CUSTOM page tracking (no autocapture)');
  return mixpanel;
};

// ===== SIMPLE TRACKING FUNCTIONS =====

// Track any event with automatic user context
export const track = (eventName, properties = {}) => {
  if (!mixpanel) return;
  
  // Get current user from localStorage or wherever you store it
  const user = getCurrentUser();
  
  const eventData = {
    ...properties,
    // Automatically add user context
    ...(user && {
      user_id: user.id,
      username: user.username,
      role: user.role,
      permissions: user.permissions
    }),
    // Add page context
    page_url: window.location.pathname,
    page_title: document.title,
    timestamp: new Date().toISOString(),
    environment: process.env.NODE_ENV,
    // Add tracking source to distinguish from autocapture
    tracking_source: 'custom'
  };
  
  mixpanel.track(eventName, eventData);

  // Enhanced logging to help debug autocapture issues
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Custom Mixpanel Event:', eventName, eventData);
  }
  
  // Log warning if we detect potential autocapture events
  if (eventName.includes('[Auto]') || eventName.includes('Element Click')) {
    console.warn('⚠️ Potential autocapture event detected:', eventName);
  }
};

// Track page views with ACTUAL page names (call this on route changes)
export const trackPageView = (pageName) => {
  const actualPageName = pageName || getPageName(window.location.pathname);
  
  // Use the actual page name as the event name for better analytics
  track(actualPageName, {
    page_name: actualPageName,
    page_path: window.location.pathname,
    page_url: window.location.href,
    page_title: document.title,
    referrer: document.referrer,
    navigation_type: 'route_change',
    event_type: 'page_view',
    // Add more context for better analytics
    user_agent: navigator.userAgent,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`
  });
};

// Track user login/logout
export const trackAuth = (action, user = null) => {
  if (action === 'login' && user) {
    mixpanel.identify(user.id);
    mixpanel.people.set({
      $name: user.username,
      $email: user.email || undefined,
      username: user.username,
      role: user.role,
      permissions: user.permissions,
      last_login: new Date().toISOString()
    });
    track('User Login', { 
      login_method: 'credentials',
      login_timestamp: new Date().toISOString()
    });
  } else if (action === 'logout') {
    track('User Logout', {
      logout_timestamp: new Date().toISOString()
    });
    mixpanel.reset();
  }
};

// Simple button click tracking (DEPRECATED - use track() with descriptive names instead)
export const trackClick = (buttonName, context = {}) => {
  track('Button Click', {
    button_name: buttonName,
    ...context
  });
};

// Enhanced button tracking with descriptive event names
export const trackButtonClick = (eventName, context = {}) => {
  track(eventName, {
    element_type: 'button',
    page_location: window.location.pathname,
    ...context
  });
};

// Helper function to add tracking attributes to buttons
export const addTrackingToButton = (buttonElement, eventName, additionalContext = {}) => {
  if (buttonElement) {
    buttonElement.setAttribute('data-track-event', eventName);
    Object.entries(additionalContext).forEach(([key, value]) => {
      buttonElement.setAttribute(`data-track-${key.replace(/_/g, '-')}`, value);
    });
  }
};

// Simple search tracking
export const trackSearch = (searchTerm, results = null) => {
  track('Search', {
    search_term: searchTerm,
    results_count: results?.length || null
  });
};

// Simple form submission tracking
export const trackForm = (formName, formData = {}) => {
  track('Form Submit', {
    form_name: formName,
    ...formData
  });
};

// Simple data export tracking
export const trackExport = (format, dataType, count = null) => {
  track('Data Export', {
    export_format: format,
    data_type: dataType,
    record_count: count
  });
};

// Simple error tracking
export const trackError = (error, context = {}) => {
  track('Error', {
    error_message: error.message || error,
    error_type: error.name || 'Unknown',
    ...context
  });
};

// ===== HELPER FUNCTIONS =====

// Get current user (adapt this to your auth system)
const getCurrentUser = () => {
  try {
    // Adapt this to however you store user data
    const userStr = localStorage.getItem('user') || sessionStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
};

// Enhanced button text extraction for Material-UI and complex components
const getButtonText = (button) => {
  if (!button) return 'Unknown Button';
  
  // Method 1: Direct text content
  let text = button.textContent?.trim();
  if (text && text.length > 0) {
    return text;
  }
  
  // Method 2: Check for aria-label
  const ariaLabel = button.getAttribute('aria-label');
  if (ariaLabel && ariaLabel.trim().length > 0) {
    return ariaLabel.trim();
  }
  
  // Method 3: Check for title attribute
  const title = button.getAttribute('title');
  if (title && title.trim().length > 0) {
    return title.trim();
  }
  
  // Method 4: Check for data attributes
  const dataLabel = button.getAttribute('data-label') || button.getAttribute('data-text');
  if (dataLabel && dataLabel.trim().length > 0) {
    return dataLabel.trim();
  }
  
  // Method 5: Look for Material-UI icon buttons and extract icon type
  if (button.classList.contains('MuiIconButton-root') || button.querySelector('[class*="MuiSvgIcon"]')) {
    const icon = button.querySelector('[class*="MuiSvgIcon"]');
    if (icon) {
      const iconClass = Array.from(icon.classList).find(cls => cls.includes('MuiSvgIcon'));
      if (iconClass) {
        // Extract icon name from class
        const iconName = iconClass.replace('MuiSvgIcon-root', '').replace('MuiSvgIcon-', '').trim();
        if (iconName) {
          return `${iconName} Icon Button`;
        }
      }
    }
    
    // Check for specific Material-UI icon patterns
    const iconElement = button.querySelector('svg');
    if (iconElement) {
      const path = iconElement.querySelector('path');
      if (path) {
        const d = path.getAttribute('d');
        // Common Material-UI icon patterns
        if (d) {
          if (d.includes('M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z')) {
            return 'Star Icon Button';
          }
          if (d.includes('M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z')) {
            return 'Add Icon Button';
          }
          if (d.includes('M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z')) {
            return 'Check Icon Button';
          }
          if (d.includes('M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z')) {
            return 'Close Icon Button';
          }
          if (d.includes('M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z')) {
            return 'Refresh Icon Button';
          }
        }
      }
    }
  }
  
  // Method 6: Check for Material-UI button with specific classes
  if (button.classList.contains('MuiButton-root')) {
    // Look for text in child elements
    const textElements = button.querySelectorAll('span, div, p');
    for (const element of textElements) {
      const elementText = element.textContent?.trim();
      if (elementText && elementText.length > 0 && !elementText.includes('Mui')) {
        return elementText;
      }
    }
  }
  
  // Method 7: Check for date picker or filter buttons
  if (button.closest('[class*="DatePicker"]') || button.closest('[class*="date"]')) {
    return 'Date Filter Button';
  }
  
  if (button.closest('[class*="Filter"]') || button.closest('[class*="filter"]')) {
    return 'Filter Button';
  }
  
  // Method 8: Check for widget buttons
  if (button.closest('[class*="Widget"]') || button.closest('[class*="widget"]')) {
    return 'Widget Button';
  }
  
  // Method 9: Check for dropdown or select buttons
  if (button.closest('[class*="Select"]') || button.closest('[class*="Dropdown"]')) {
    return 'Dropdown Button';
  }
  
  // Method 10: Check for navigation buttons
  if (button.closest('[class*="Navigation"]') || button.closest('[class*="nav"]')) {
    return 'Navigation Button';
  }
  
  // Method 11: Check for action buttons based on position
  const rect = button.getBoundingClientRect();
  if (rect.width < 50 && rect.height < 50) {
    return 'Small Action Button';
  }
  
  // Method 12: Check for specific button types
  if (button.type === 'submit') {
    return 'Submit Button';
  }
  if (button.type === 'reset') {
    return 'Reset Button';
  }
  
  // Method 13: Check for role-based identification
  const role = button.getAttribute('role');
  if (role) {
    return `${role.charAt(0).toUpperCase() + role.slice(1)} Button`;
  }
  
  // Fallback: Use button type or class information
  const buttonType = button.type || 'button';
  const hasIcon = button.querySelector('svg, i, [class*="icon"]');
  if (hasIcon) {
    return 'Icon Button';
  }
  
  return 'Button';
};

// Generate descriptive event names based on button context
const getDescriptiveEventName = (button, buttonText) => {
  const page = window.location.pathname;
  const buttonTextLower = buttonText.toLowerCase();
  
  // Common button patterns and their descriptive names
  const buttonPatterns = {
    // Navigation buttons
    'back': 'Navigate Back',
    'next': 'Navigate Next',
    'previous': 'Navigate Previous',
    'home': 'Navigate to Home',
    'dashboard': 'Navigate to Dashboard',
    
    // Action buttons
    'save': 'Save Data',
    'submit': 'Submit Form',
    'cancel': 'Cancel Action',
    'delete': 'Delete Item',
    'edit': 'Edit Item',
    'add': 'Add Item',
    'create': 'Create Item',
    'update': 'Update Item',
    'refresh': 'Refresh Data',
    'reload': 'Reload Page',
    'search': 'Search Action',
    'filter': 'Apply Filter',
    'clear': 'Clear Filter',
    'reset': 'Reset Form',
    'download': 'Download Data',
    'export': 'Export Data',
    'import': 'Import Data',
    'upload': 'Upload File',
    'login': 'User Login',
    'logout': 'User Logout',
    'sign in': 'User Sign In',
    'sign out': 'User Sign Out',
    
    // Toggle buttons
    'toggle': 'Toggle View',
    'show': 'Show Details',
    'hide': 'Hide Details',
    'expand': 'Expand Section',
    'collapse': 'Collapse Section',
    'open': 'Open Dialog',
    'close': 'Close Dialog',
    
    // Data management
    'view': 'View Details',
    'details': 'View Details',
    'info': 'View Information',
    'settings': 'Open Settings',
    'configure': 'Configure Settings',
    'manage': 'Manage Items',
    'select': 'Select Item',
    'choose': 'Choose Option',
    
    // Material-UI specific patterns
    'icon button': 'Icon Button Clicked',
    'date filter button': 'Date Filter Clicked',
    'filter button': 'Filter Button Clicked',
    'widget button': 'Widget Button Clicked',
    'dropdown button': 'Dropdown Button Clicked',
    'navigation button': 'Navigation Button Clicked',
    'small action button': 'Small Action Button Clicked',
    'submit button': 'Submit Button Clicked',
    'reset button': 'Reset Button Clicked',
    'button': 'Button Clicked'
  };
  
  // Check for exact matches first
  for (const [pattern, eventName] of Object.entries(buttonPatterns)) {
    if (buttonTextLower.includes(pattern)) {
      return eventName;
    }
  }
  
  // Page-specific event naming
  if (page.includes('/products')) {
    if (buttonTextLower.includes('download') || buttonTextLower.includes('csv')) {
      return 'Download Product Data';
    }
    if (buttonTextLower.includes('expand') || buttonTextLower.includes('details')) {
      return 'View Product Details';
    }
    if (buttonTextLower.includes('filter') || buttonTextLower.includes('search')) {
      return 'Filter Products';
    }
    if (buttonTextLower.includes('date filter') || buttonTextLower.includes('date')) {
      return 'Filter Products by Date';
    }
  }
  
  if (page.includes('/clients')) {
    if (buttonTextLower.includes('download') || buttonTextLower.includes('csv')) {
      return 'Download Client Data';
    }
    if (buttonTextLower.includes('expand') || buttonTextLower.includes('details')) {
      return 'View Client Details';
    }
    if (buttonTextLower.includes('filter') || buttonTextLower.includes('search')) {
      return 'Filter Clients';
    }
    if (buttonTextLower.includes('date filter') || buttonTextLower.includes('date')) {
      return 'Filter Clients by Date';
    }
  }
  
  if (page.includes('/hosts')) {
    if (buttonTextLower.includes('download') || buttonTextLower.includes('csv')) {
      return 'Download Host Data';
    }
    if (buttonTextLower.includes('expand') || buttonTextLower.includes('details')) {
      return 'View Host Details';
    }
    if (buttonTextLower.includes('filter') || buttonTextLower.includes('search')) {
      return 'Filter Hosts';
    }
    if (buttonTextLower.includes('date filter') || buttonTextLower.includes('date')) {
      return 'Filter Hosts by Date';
    }
  }
  
  if (page.includes('/audit-logs')) {
    if (buttonTextLower.includes('download') || buttonTextLower.includes('csv')) {
      return 'Download Audit Logs';
    }
    if (buttonTextLower.includes('filter') || buttonTextLower.includes('search')) {
      return 'Filter Audit Logs';
    }
    if (buttonTextLower.includes('date filter') || buttonTextLower.includes('date')) {
      return 'Filter Audit Logs by Date';
    }
  }
  
  if (page.includes('/oci')) {
    if (buttonTextLower.includes('refresh')) {
      return 'Refresh OCI Data';
    }
    if (buttonTextLower.includes('download') || buttonTextLower.includes('csv')) {
      return 'Download OCI Data';
    }
  }
  
  // Default fallback - create a more descriptive name
  const cleanText = buttonText.replace(/[^\w\s]/g, '').trim();
  if (cleanText.length > 0) {
    return `${cleanText} Button Clicked`;
  }
  
  return 'Button Clicked';
};

// Get page name from path - ENHANCED with more specific names
const getPageName = (path) => {
  const routes = {
    '/': 'Dashboard',
    '/hosts': 'Hosts Management',
    '/clients': 'Clients Management',
    '/products': 'Products Management',
    '/alerts': 'Alerts & Notifications',
    '/oci': 'OCI Dashboard',
    '/oci/instances': 'OCI Instances',
    '/oci/costs': 'OCI Cost Analysis',
    '/oci/storage': 'OCI Storage Dashboard',
    '/esdl-monitoring': 'ESDL Monitoring Dashboard',
    '/opensearch-monitoring': 'OpenSearch Monitoring',
    '/settings': 'Application Settings',
    '/users': 'User Management',
    '/audit-logs': 'Audit Logs',
    '/login': 'User Login',
    '/mixpanel-demo': 'Mixpanel Demo'
  };

  // Handle dynamic routes with better naming
  if (path.startsWith('/region/')) {
    const regionId = path.split('/')[2];
    return `Region Dashboard - ${regionId}`;
  }
  if (path.startsWith('/esdl-monitoring/host/')) {
    const hostname = path.split('/')[3];
    return `ESDL Host Details - ${hostname}`;
  }
  
  return routes[path] || `Custom Page: ${path}`;
};

// ===== AUTOMATIC TRACKING SETUP =====

// Auto-track page views on navigation (call this once)
export const setupAutoTracking = () => {
  // Track initial page load
  trackPageView();

  // Track navigation changes (for SPAs) - ENHANCED
  let lastPath = window.location.pathname;
  
  // Method 1: Use History API to detect route changes
  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;
  
  window.history.pushState = function(...args) {
    originalPushState.apply(this, args);
    if (window.location.pathname !== lastPath) {
      lastPath = window.location.pathname;
      trackPageView();
    }
  };
  
  window.history.replaceState = function(...args) {
    originalReplaceState.apply(this, args);
    if (window.location.pathname !== lastPath) {
      lastPath = window.location.pathname;
      trackPageView();
    }
  };
  
  // Method 2: Listen for popstate events (browser back/forward)
  window.addEventListener('popstate', () => {
    if (window.location.pathname !== lastPath) {
      lastPath = window.location.pathname;
      trackPageView();
    }
  });

  // Method 3: Use MutationObserver as backup
  const observer = new MutationObserver(() => {
    if (window.location.pathname !== lastPath) {
      lastPath = window.location.pathname;
      trackPageView();
    }
  });
  
  observer.observe(document.body, { childList: true, subtree: true });

  // Track errors automatically
  window.addEventListener('error', (event) => {
    trackError(event.error || event.message, {
      filename: event.filename,
      line: event.lineno
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    trackError(event.reason, { type: 'unhandled_promise' });
  });

  // Setup custom UI interaction tracking
  setupAutoUITracking();

  console.log('✅ Custom page tracking enabled - No more auto-generated events!');
};

// Enhanced UI interaction tracking - CUSTOM implementation
// since we're not using Mixpanel's autocapture anymore
const setupAutoUITracking = () => {
  // Add custom event listeners for important interactions
  document.addEventListener('click', (event) => {
    const target = event.target;
    
    // Track button clicks with improved event naming
    if (target.tagName === 'BUTTON' || target.closest('button')) {
      const button = target.tagName === 'BUTTON' ? target : target.closest('button');
      const buttonText = getButtonText(button);
      
      // Get more context from data attributes
      const eventName = button.getAttribute('data-track-event') || 
                       button.getAttribute('data-mixpanel-event') ||
                       getDescriptiveEventName(button, buttonText);
      
      const context = {
        element_type: 'button',
        page_location: window.location.pathname,
        button_text: buttonText,
        button_id: button.id || null,
        button_class: button.className || null,
        button_type: button.type || null,
        button_role: button.getAttribute('role') || null
      };
      
      // Add any additional data attributes as context
      Array.from(button.attributes).forEach(attr => {
        if (attr.name.startsWith('data-track-') && attr.name !== 'data-track-event') {
          const key = attr.name.replace('data-track-', '').replace(/-/g, '_');
          context[key] = attr.value;
        }
      });
      
      track(eventName, context);
    }
    
    // Track link clicks
    if (target.tagName === 'A' || target.closest('a')) {
      const link = target.tagName === 'A' ? target : target.closest('a');
      const linkText = link.textContent?.trim() || 'Unknown Link';
      const linkHref = link.href || '';
      
      const eventName = link.getAttribute('data-track-event') || 
                       link.getAttribute('data-mixpanel-event') ||
                       'Link Click';
      
      track(eventName, {
        link_text: linkText,
        link_url: linkHref,
        page_location: window.location.pathname
      });
    }
  });

  // Track form submissions
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const formName = form.getAttribute('data-form-name') || form.id || 'Unknown Form';
    trackForm(formName, {
      page_location: window.location.pathname
    });
  });

  console.log('✅ Custom UI interaction tracking enabled');
};

// Add session recording functionality (DISABLED to prevent autocapture)
export const startSessionRecording = () => {
  if (!mixpanel) return;
  
  // Session recording is disabled to prevent [Auto] Element Click events
  console.log('ℹ️ Session recording disabled to prevent autocapture events');
  
  // If you need session recording, you can enable it manually with:
  // if (mixpanel.session_recording && typeof mixpanel.session_recording.start === 'function') {
  //   mixpanel.session_recording.start();
  // }
};

// Add heatmap tracking functionality (DISABLED to prevent autocapture)
export const enableHeatmapTracking = () => {
  if (!mixpanel) return;
  
  // Heatmap tracking is disabled to prevent [Auto] Element Click events
  console.log('ℹ️ Heatmap tracking disabled to prevent autocapture events');
  
  // If you need heatmap tracking, you can enable it manually with:
  // mixpanel.config.record_heatmap_data = true;
};

// Debug function to check autocapture status
export const checkAutocaptureStatus = () => {
  if (!mixpanel) {
    console.log('❌ Mixpanel not initialized');
    return;
  }
  
  console.log('🔍 Mixpanel Autocapture Status:');
  console.log('- autocapture:', mixpanel.config?.autocapture);
  console.log('- track_links:', mixpanel.config?.track_links);
  console.log('- track_forms:', mixpanel.config?.track_forms);
  console.log('- track_pageview:', mixpanel.config?.track_pageview);
  console.log('- record_sessions_percent:', mixpanel.config?.record_sessions_percent);
  console.log('- record_heatmap_data:', mixpanel.config?.record_heatmap_data);
};

// Force disable autocapture (call this if autocapture is still running)
export const forceDisableAutocapture = () => {
  if (!mixpanel) return;
  
  // Disable all autocapture features
  if (mixpanel.config) {
    mixpanel.config.autocapture = false;
    mixpanel.config.track_links = false;
    mixpanel.config.track_forms = false;
    mixpanel.config.track_pageview = false;
    mixpanel.config.record_sessions_percent = 0;
    mixpanel.config.record_heatmap_data = false;
    mixpanel.config.track_performance = false;
  }
  
  // Also try to disable on the global mixpanel object
  if (window.mixpanel && window.mixpanel.config) {
    window.mixpanel.config.autocapture = false;
    window.mixpanel.config.track_links = false;
    window.mixpanel.config.track_forms = false;
    window.mixpanel.config.track_pageview = false;
  }
  
  console.log('🔒 Autocapture forcefully disabled');
  checkAutocaptureStatus();
};

// Export everything for easy use
export default {
  init: initMixpanel,
  track,
  trackPageView,
  trackAuth,
  trackClick,
  trackButtonClick,
  addTrackingToButton,
  trackSearch,
  trackForm,
  trackExport,
  trackError,
  setupAutoTracking,
  startSessionRecording,
  enableHeatmapTracking,
  checkAutocaptureStatus,
  forceDisableAutocapture
};